import { OTPInput as BaseOTPInput, type OTPInputProps, type SlotProps } from 'input-otp'
import type { SetOptional } from 'type-fest'

import style from './OTPInput.module.scss'

function Slot({ char, isActive, placeholderChar }: SlotProps) {
  return (
    <div className={clsx(style.Slot, isActive && style.IsActiveSlot)}>
      <div className={style.Char}>{char ?? placeholderChar}</div>
    </div>
  )
}

/**
 * A reusable OTP (One-Time Password) input component.
 * It is a wrapper around the `input-otp` library with default styling.
 *
 * @param {Omit<OTPInputProps, 'render' | 'children'>} props The props for the component.
 * @see https://github.com/guilherme-g-s/input-otp
 */
export default memo(function OTPInput(props: SetOptional<Omit<OTPInputProps, 'render' | 'children'>, 'maxLength'>) {
  const { maxLength = 6, containerClassName, inputMode = 'numeric', ...rest } = props
  return (
    <BaseOTPInput
      maxLength={maxLength}
      inputMode={inputMode}
      {...rest}
      // eslint-disable-next-line jsx-a11y/no-autofocus -- this is to disable auto focus
      autoFocus={false}
      containerClassName={clsx(style.OTPInput, containerClassName)}
      render={({ slots }) => (
        <div className={style.Container}>
          {slots.slice(0, maxLength).map((slot, idx) => (
            // eslint-disable-next-line react/no-array-index-key -- Using index as a key is safe here because the number of slots is fixed and they are not reordered.
            <Slot key={idx} {...slot} />
          ))}
        </div>
      )}
    />
  )
})
